/**
 * Site-wide MobileOptimizer utility for responsive design detection and optimization
 * Provides consistent mobile optimization patterns across all pages
 */

/**
 * Responsive design breakpoints for entire site
 */
export const RESPONSIVE_BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1200,
  largeDesktop: 1600
};

/**
 * Touch optimization settings
 */
export const TOUCH_OPTIMIZATIONS = {
  minTouchTarget: 44, // Minimum touch target size in pixels
  touchPadding: 8,    // Additional padding for touch targets
  swipeThreshold: 50, // Minimum distance for swipe gestures
  tapDelay: 300       // Maximum delay for tap vs long press
};

/**
 * Device detection utility
 */
export class DeviceDetector {
  static isMobile() {
    return window.innerWidth <= RESPONSIVE_BREAKPOINTS.mobile;
  }

  static isTablet() {
    return window.innerWidth > RESPONSIVE_BREAKPOINTS.mobile && 
           window.innerWidth <= RESPONSIVE_BREAKPOINTS.tablet;
  }

  static isDesktop() {
    return window.innerWidth > RESPONSIVE_BREAKPOINTS.tablet;
  }

  static isLargeDesktop() {
    return window.innerWidth > RESPONSIVE_BREAKPOINTS.desktop;
  }

  static getDeviceType() {
    if (this.isMobile()) return 'mobile';
    if (this.isTablet()) return 'tablet';
    if (this.isLargeDesktop()) return 'large-desktop';
    return 'desktop';
  }

  static getOrientation() {
    return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
  }

  static isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }
}

/**
 * Viewport utilities for responsive behavior
 */
export class ViewportManager {
  static getViewportSize() {
    return {
      width: window.innerWidth,
      height: window.innerHeight
    };
  }

  static getBreakpointInfo() {
    const width = window.innerWidth;
    const breakpoints = RESPONSIVE_BREAKPOINTS;
    
    return {
      current: DeviceDetector.getDeviceType(),
      isMobile: width <= breakpoints.mobile,
      isTablet: width > breakpoints.mobile && width <= breakpoints.tablet,
      isDesktop: width > breakpoints.tablet && width <= breakpoints.desktop,
      isLargeDesktop: width > breakpoints.desktop,
      width,
      height: window.innerHeight
    };
  }

  static addResizeListener(callback) {
    const handleResize = () => {
      callback(this.getBreakpointInfo());
    };
    
    window.addEventListener('resize', handleResize);
    
    // Return cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }

  static addOrientationListener(callback) {
    const handleOrientationChange = () => {
      // Small delay to ensure dimensions are updated
      setTimeout(() => {
        callback({
          orientation: DeviceDetector.getOrientation(),
          viewport: this.getViewportSize()
        });
      }, 100);
    };
    
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);
    
    // Return cleanup function
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }
}

/**
 * Touch optimization utilities
 */
export class TouchOptimizer {
  static optimizeElement(element) {
    if (!element || !DeviceDetector.isTouchDevice()) return;
    
    const style = element.style;
    const minSize = TOUCH_OPTIMIZATIONS.minTouchTarget;
    const padding = TOUCH_OPTIMIZATIONS.touchPadding;
    
    // Ensure minimum touch target size
    if (element.offsetWidth < minSize) {
      style.minWidth = `${minSize}px`;
    }
    if (element.offsetHeight < minSize) {
      style.minHeight = `${minSize}px`;
    }
    
    // Add touch padding
    if (!style.padding) {
      style.padding = `${padding}px`;
    }
    
    // Improve touch responsiveness
    style.touchAction = 'manipulation';
    style.userSelect = 'none';
    style.webkitTapHighlightColor = 'transparent';
  }

  static addTouchFeedback(element, options = {}) {
    if (!element || !DeviceDetector.isTouchDevice()) return;
    
    const {
      activeClass = 'touch-active',
      duration = 150
    } = options;
    
    const addActiveState = () => {
      element.classList.add(activeClass);
    };
    
    const removeActiveState = () => {
      element.classList.remove(activeClass);
    };
    
    element.addEventListener('touchstart', addActiveState, { passive: true });
    element.addEventListener('touchend', () => {
      setTimeout(removeActiveState, duration);
    }, { passive: true });
    element.addEventListener('touchcancel', removeActiveState, { passive: true });
    
    // Return cleanup function
    return () => {
      element.removeEventListener('touchstart', addActiveState);
      element.removeEventListener('touchend', removeActiveState);
      element.removeEventListener('touchcancel', removeActiveState);
    };
  }
}

/**
 * Modal positioning utilities for mobile-friendly display
 */
export class ModalPositioner {
  static getOptimalPosition(modalElement, options = {}) {
    if (!modalElement) return {};
    
    const viewport = ViewportManager.getViewportSize();
    const isMobile = DeviceDetector.isMobile();
    
    const {
      margin = 20,
      maxWidth = isMobile ? viewport.width - (margin * 2) : 600,
      maxHeight = viewport.height - (margin * 2)
    } = options;
    
    if (isMobile) {
      // On mobile, use full-screen or near-full-screen modals
      return {
        position: 'fixed',
        top: `${margin}px`,
        left: `${margin}px`,
        right: `${margin}px`,
        bottom: `${margin}px`,
        maxWidth: 'none',
        maxHeight: 'none',
        width: 'auto',
        height: 'auto',
        transform: 'none'
      };
    }
    
    // Desktop positioning
    return {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      maxWidth: `${maxWidth}px`,
      maxHeight: `${maxHeight}px`,
      width: 'auto',
      height: 'auto'
    };
  }

  static applyMobileOptimizations(modalElement) {
    if (!modalElement || !DeviceDetector.isMobile()) return;
    
    const style = modalElement.style;
    
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    
    // Optimize for mobile viewport
    style.overflowY = 'auto';
    style.webkitOverflowScrolling = 'touch';
    
    // Return cleanup function
    return () => {
      document.body.style.overflow = '';
    };
  }
}

/**
 * Main MobileOptimizer class that combines all utilities
 */
export class MobileOptimizer {
  constructor() {
    this.deviceDetector = DeviceDetector;
    this.viewportManager = ViewportManager;
    this.touchOptimizer = TouchOptimizer;
    this.modalPositioner = ModalPositioner;
  }

  /**
   * Initialize mobile optimizations for the entire site
   */
  static initialize() {
    // Add mobile-specific CSS classes to body
    const updateBodyClasses = () => {
      const body = document.body;
      const deviceType = DeviceDetector.getDeviceType();
      const orientation = DeviceDetector.getOrientation();
      const isTouchDevice = DeviceDetector.isTouchDevice();
      
      // Remove existing classes
      body.classList.remove('mobile', 'tablet', 'desktop', 'large-desktop', 'portrait', 'landscape', 'touch', 'no-touch');
      
      // Add current classes
      body.classList.add(deviceType);
      body.classList.add(orientation);
      body.classList.add(isTouchDevice ? 'touch' : 'no-touch');
    };
    
    // Initial setup
    updateBodyClasses();
    
    // Update on resize and orientation change
    ViewportManager.addResizeListener(updateBodyClasses);
    ViewportManager.addOrientationListener(updateBodyClasses);
    
    // Add CSS custom properties for breakpoints
    const root = document.documentElement;
    Object.entries(RESPONSIVE_BREAKPOINTS).forEach(([key, value]) => {
      root.style.setProperty(`--breakpoint-${key}`, `${value}px`);
    });
  }

  /**
   * Get current mobile optimization state
   */
  static getOptimizationState() {
    return {
      device: {
        type: DeviceDetector.getDeviceType(),
        isMobile: DeviceDetector.isMobile(),
        isTablet: DeviceDetector.isTablet(),
        isDesktop: DeviceDetector.isDesktop(),
        isTouchDevice: DeviceDetector.isTouchDevice(),
        orientation: DeviceDetector.getOrientation()
      },
      viewport: ViewportManager.getViewportSize(),
      breakpoints: ViewportManager.getBreakpointInfo(),
      touchOptimizations: TOUCH_OPTIMIZATIONS
    };
  }
}